import ApprovalTypeTag from "@/components/tags/ApprovalTypeTag";
import ViewOnlyLeaveRequest from "@/components/leave-request/ViewOnlyLeaveRequest";
import ViewOnlyLeaveRequestDialog from "@/components/leave-request/ViewOnlyLeaveRequestDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ApprovalType, LeaveRequestDTO } from "@/models/leave-requests.model";
import { ProcessingStatus } from "@/models/payslips.model";
import { toDateFormatted } from "@/utils/date-format";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown, Calendar, MoreHorizontal, Trash2 } from "lucide-react";
import React from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { capitalizeFirstLetter } from "@/utils/string-format";
import { toast } from "@/hooks/use-toast";
import { useAppDispatch } from "@/app/hooks";
import { fetchLeaveRequestsOfUser, fetchUserLeaveRequestsCount, removeLeaveRequest } from "@/features/leaveRequests/leaveRequestSlice";
import { useCurrentUser } from "@/context/UserContext";

const ActionCell = ({
  row,
}: {
  row: any;
}) => {
  const status = row.original.status as ApprovalType;
  const dispatch = useAppDispatch();
  const { currentUser } = useCurrentUser();


  const [open, setOpen] = React.useState(false);

  const handleDelete = async () => {
    try {
      if (!currentUser) return;
      await dispatch(removeLeaveRequest(row.original.id));
      toast({
        title: "Leave request deleted",
        description: "The leave request has been deleted successfully.",
      });
      await dispatch(fetchLeaveRequestsOfUser({ userId: currentUser.id }))
      await dispatch(fetchUserLeaveRequestsCount(currentUser.id))
      setOpen(false);

    } catch (error: any) {
      console.error(error);
      toast({
        variant: "destructive",
        title: error.message || "Error",
        description: error.error || "An unknown error occurred",
      })
    }
  };
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => setOpen(true)}>
          <Calendar /> View
        </DropdownMenuItem>
        {status === ApprovalType.PENDING && (
          <DropdownMenuItem onClick={handleDelete}>
            <Trash2 />
            Delete Request
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
      <Sheet open={open} onOpenChange={setOpen}>
        <ViewOnlyLeaveRequest
          isOpen={open}
          handleDelete={handleDelete}
          requestObj={row.original}
        />
      </Sheet>
    </DropdownMenu>
  );
};

export const getColumns = (
): ColumnDef<LeaveRequestDTO>[] => [
    {
      id: "type",
      size: 150,
      accessorFn: (row) => row.type,
      header: "Leave Type",
      cell: ({ row }) => {
        return <div className="min-w-[200px]">
          <span>{capitalizeFirstLetter(row.original.type)}</span>
        </div>
      },
      filterFn: (row, id, value) => {
        return value.includes(String(row.getValue(id)).toUpperCase());
      },
    },
    {
      id: "startDate",
      cell: ({ row }) => {
        return (
          <span>
            {toDateFormatted(row.original.fromDate)}{" "}
            {row.original.fromDateIsAm ? "AM" : "PM"}
          </span>
        );
      },
      accessorFn: (row) => row.fromDate,
      header: ({ column }) => {
        return (
          <div className="flex items-center gap-2">
            Start Date
            <button
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            >
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    {
      id: "endDate",
      cell: ({ row }) => {
        return (
          <span>
            {toDateFormatted(row.original.toDate)}{" "}
            {row.original.toDateIsAm ? "AM" : "PM"}
          </span>
        );
      },
      accessorFn: (row) => row.toDate,
      header: ({ column }) => {
        return (
          <div className="flex items-center gap-2">
            End Date
            <button
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            >
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.days,
      header: "Days",
      cell: ({ row }) => {

        return <div className="min-w-[90px]"><span className="w-full">{row.original.days}</span></div>;
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        return <ApprovalTypeTag status={row.original.status} />;
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: "requestedAt",
      cell: ({ row }) => {
        return <span>{toDateFormatted(row.original.createdAt)}</span>;
      },
      accessorFn: (row) => row.createdAt,
      header: ({ column }) => {
        return (
          <div className="flex items-center gap-2">
            Requested On
            <button
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            >
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    {
      id: "action",
      cell: ({ row }) => (
        <ActionCell row={row} />
      ),
    },
  ];
